import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { Button, IconButton, TextField, InputAdornment, Tooltip, CircularProgress } from "@material-ui/core";
import { Add, Search, Refresh, EditOutlined, DeleteOutline, AttachFile, FlashOn, ChevronLeft, ChevronRight, PeopleOutline, LockOutlined, DescriptionOutlined } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import "./quick-replies.css";

export default function QuickMessages() {
  const { user, socket } = useContext(AuthContext);
  const [records, setRecords] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [editor, setEditor] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => setRevision(value => value + 1);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setFailed(false);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/quick-messages", { params: { searchParam: search.trim(), pageNumber: page } });
        if (!current) return;
        const lastPage = Math.max(1, Math.ceil(data.count / 20));
        if (page > lastPage) { setPage(lastPage); return; }
        setRecords(data.records);
        setCount(data.count);
      } catch (error) {
        if (current) { setFailed(true); toastError(error); }
      } finally { if (current) setLoading(false); }
    }, 300);
    return () => { current = false; clearTimeout(timer); };
  }, [search, page, revision, user.companyId]);

  useEffect(() => {
    if (!socket) return;
    const event = `company-${user.companyId}-quickmessage`;
    const onChange = () => setRevision(value => value + 1);
    socket.on(event, onChange);
    return () => socket.off(event, onChange);
  }, [socket, user.companyId]);

  const remove = async () => {
    if (!deleting || busy) return;
    setBusy(true);
    try {
      await api.delete(`/quick-messages/${deleting.id}`);
      toast.success("Resposta rápida excluída.");
      setDeleting(null);
      refresh();
    } catch (error) { toastError(error); }
    finally { setBusy(false); }
  };
  const pages = Math.max(1, Math.ceil(count / 20));
  const attached = records.filter(record => record.mediaName || record.mediaPath).length;
  const shared = records.filter(record => record.visao).length;
  const editable = record => !(record.visao && !record.geral && record.userId !== user.id);

  return (
    <section className="qr-page" aria-label="Respostas rápidas">
      <header className="qr-heading">
        <div><h1><span className="qr-heading-icon"><FlashOn /></span>Respostas rápidas</h1><p>Organize seus atalhos, mensagens e anexos para agilizar cada atendimento.</p></div>
        <Button className="qr-primary" variant="contained" disableElevation startIcon={<Add />} onClick={() => setEditor({})}>Nova resposta rápida</Button>
      </header>
      <div className="qr-summary">
        <div className="qr-stat"><span>{search ? "Atalhos encontrados" : "Total de atalhos"}</span><strong>{loading || failed ? "—" : count}</strong><small>Disponíveis para seu usuário</small></div>
        <div className="qr-stat"><span>Com anexos nesta página</span><strong>{loading || failed ? "—" : attached}</strong><small>Imagens, documentos, vídeos e áudios</small></div>
        <div className="qr-stat"><span>Compartilhados nesta página</span><strong>{loading || failed ? "—" : shared}</strong><small>Visíveis para outros usuários</small></div>
        <aside className="qr-tip"><FlashOn /><div><strong>Menos digitação, mais atenção</strong><p>Use os atalhos salvos no atendimento e personalize a mensagem com as variáveis do cliente.</p></div></aside>
      </div>
      <div className="qr-toolbar">
        <TextField variant="outlined" size="small" type="search" placeholder="Buscar pelo atalho..." value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} inputProps={{ "aria-label": "Buscar pelo atalho" }} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
        <span className="qr-toolbar-note">Sua biblioteca de respostas</span>
        <Tooltip title="Atualizar respostas"><span><IconButton aria-label="Atualizar respostas" disabled={loading} onClick={refresh}><Refresh /></IconButton></span></Tooltip>
      </div>
      <div className="qr-library" aria-busy={loading}>
        <div className="qr-table-scroll"><table className="qr-table">
          <thead><tr><th>Atalho</th><th>Mensagem / conteúdo prévio</th><th>Mídia / anexo</th><th>Visibilidade</th><th className="qr-actions-heading">Ações</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5}><div className="qr-empty" role="status"><CircularProgress size={26} /><p>Carregando respostas…</p></div></td></tr> : failed ? <tr><td colSpan={5}><div className="qr-empty" role="alert"><strong>Não foi possível carregar as respostas.</strong><Button onClick={refresh}>Tentar novamente</Button></div></td></tr> : records.length === 0 ? <tr><td colSpan={5}><div className="qr-empty"><DescriptionOutlined /><h2>{search ? "Nenhum atalho encontrado" : "Sua primeira resposta começa aqui"}</h2><p>{search ? "Tente buscar por outro atalho." : "Salve mensagens que você usa com frequência e tenha tudo à mão."}</p><Button className="qr-primary" variant="contained" disableElevation onClick={() => search ? setSearch("") : setEditor({})}>{search ? "Limpar busca" : "Criar resposta rápida"}</Button></div></td></tr> : records.map(record => (
              <tr key={record.id}>
                <td data-label="Atalho"><button className="qr-shortcut" onClick={() => setEditor(record)} aria-label={`Abrir resposta ${record.shortcode}`}>{record.shortcode}</button></td>
                <td data-label="Mensagem"><p className="qr-message" title={record.message}>{record.message || "Resposta com anexo, sem texto."}</p></td>
                <td data-label="Mídia / anexo">{record.mediaName || record.mediaPath ? <span className={`qr-media qr-media-${record.mediaType || "document"}`} title={record.mediaName}><AttachFile fontSize="small" /><span>{record.mediaName || "Anexo"}</span></span> : <span className="qr-muted">Sem anexo</span>}</td>
                <td data-label="Visibilidade"><span className={`qr-visibility ${record.visao ? "qr-shared" : ""}`}>{record.visao ? <PeopleOutline /> : <LockOutlined />}{record.visao ? "Compartilhada" : "Pessoal"}</span></td>
                <td data-label="Ações"><div className="qr-actions"><Tooltip title={editable(record) ? "Editar resposta" : "Visualizar resposta"}><IconButton aria-label={`Editar ${record.shortcode}`} onClick={() => setEditor(record)}><EditOutlined fontSize="small" /></IconButton></Tooltip><Tooltip title="Excluir resposta"><span><IconButton aria-label={`Excluir ${record.shortcode}`} disabled={busy || !editable(record)} onClick={() => setDeleting(record)}><DeleteOutline fontSize="small" /></IconButton></span></Tooltip></div></td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <footer className="qr-footer"><span aria-live="polite">{!loading && !failed && (count ? `Mostrando ${(page - 1) * 20 + 1} a ${Math.min(page * 20, count)} de ${count} respostas` : "Nenhuma resposta para exibir")}</span><nav aria-label="Paginação de respostas"><Button disabled={page === 1 || loading} onClick={() => setPage(value => value - 1)} startIcon={<ChevronLeft />}>Anterior</Button><span className="qr-page-number">{page} / {pages}</span><Button disabled={page >= pages || loading} onClick={() => setPage(value => value + 1)} endIcon={<ChevronRight />}>Próxima</Button></nav></footer>
      </div>
      {editor !== null && <QuickMessageDialog open onClose={() => setEditor(null)} quickemessageId={editor.id} reload={refresh} />}
      <ConfirmationModal title={`Excluir resposta ${deleting?.shortcode || ""}?`} open={Boolean(deleting)} onClose={() => !busy && setDeleting(null)} onConfirm={remove}>Esta resposta será removida da biblioteca. Confirme apenas se ela não for mais necessária.</ConfirmationModal>
    </section>
  );
}
